import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const PerformanceMonitor = ({ onQualityChange }) => {
  const [fps, setFps] = useState(60);
  const [quality, setQuality] = useState('high');
  const [isVisible, setIsVisible] = useState(false);
  const [deviceInfo, setDeviceInfo] = useState({});

  useEffect(() => {
    // Detect device capabilities
    const detectDevice = () => {
      const canvas = document.createElement('canvas');
      const gl = canvas?.getContext('webgl') || canvas?.getContext('experimental-webgl');
      
      const info = {
        webgl: !!gl,
        mobile: /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i?.test(navigator.userAgent),
        memory: navigator.deviceMemory || 4,
        cores: navigator.hardwareConcurrency || 4,
        pixelRatio: window.devicePixelRatio || 1
      };

      setDeviceInfo(info);
      
      // Auto-adjust quality based on device
      let autoQuality = 'high';
      if (info?.mobile || info?.memory < 4 || info?.cores < 4) {
        autoQuality = 'medium';
      }
      if (info?.memory < 2 || info?.cores < 2 || !info?.webgl) {
        autoQuality = 'low';
      }
      
      setQuality(autoQuality);
      onQualityChange(autoQuality);
    };

    detectDevice();
  }, [onQualityChange]);

  useEffect(() => {
    // FPS monitoring
    let frameCount = 0;
    let lastTime = performance.now();
    
    const measureFPS = () => {
      frameCount++;
      const currentTime = performance.now();
      
      if (currentTime - lastTime >= 1000) {
        const currentFPS = Math.round((frameCount * 1000) / (currentTime - lastTime));
        setFps(currentFPS);
        
        // Auto-adjust quality based on FPS
        if (currentFPS < 30 && quality === 'high') {
          setQuality('medium');
          onQualityChange('medium');
        } else if (currentFPS < 20 && quality === 'medium') {
          setQuality('low');
          onQualityChange('low');
        }
        
        frameCount = 0;
        lastTime = currentTime;
      }
      
      requestAnimationFrame(measureFPS);
    };
    
    measureFPS();
  }, [quality, onQualityChange]);

  const qualitySettings = {
    low: { label: 'Low', color: '#ff4757', particles: 50, effects: false },
    medium: { label: 'Medium', color: '#ffaa00', particles: 100, effects: true },
    high: { label: 'High', color: '#00ff88', particles: 150, effects: true }
  };

  const handleQualityChange = (newQuality) => {
    setQuality(newQuality);
    onQualityChange(newQuality);
  };

  return (
    <div className="fixed bottom-6 right-6 z-50">
      {/* Toggle Button */}
      <button
        onClick={() => setIsVisible(!isVisible)}
        className="w-12 h-12 glass-panel rounded-full flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors cosmic-glow-hover"
      >
        <Icon name="Settings" size={20} />
      </button>
      {/* Performance Panel */}
      {isVisible && (
        <div className="absolute bottom-16 right-0 w-80 glass-panel rounded-lg p-4 shadow-cosmic-lg">
          <div className="space-y-4">
            {/* Header */}
            <div className="flex items-center justify-between">
              <h3 className="font-headline font-bold text-lg text-foreground">
                Performance
              </h3>
              <button
                onClick={() => setIsVisible(false)}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <Icon name="X" size={16} />
              </button>
            </div>

            {/* FPS Display */}
            <div className="flex items-center justify-between">
              <span className="font-body text-sm text-muted-foreground">FPS:</span>
              <div className="flex items-center space-x-2">
                <div 
                  className="w-2 h-2 rounded-full"
                  style={{ 
                    backgroundColor: fps >= 50 ? '#00ff88' : fps >= 30 ? '#ffaa00' : '#ff4757' 
                  }}
                />
                <span className="font-accent text-sm text-foreground">{fps}</span>
              </div>
            </div>

            {/* Quality Settings */}
            <div className="space-y-2">
              <span className="font-body text-sm text-muted-foreground">Quality:</span>
              <div className="grid grid-cols-3 gap-2">
                {Object.entries(qualitySettings)?.map(([key, setting]) => (
                  <button
                    key={key}
                    onClick={() => handleQualityChange(key)}
                    className={`px-3 py-2 rounded-lg text-xs font-medium transition-all duration-200 ${
                      quality === key
                        ? 'bg-accent/20 text-accent border border-accent/30' :'bg-surface/50 text-muted-foreground hover:text-foreground border border-border/30'
                    }`}
                  >
                    {setting?.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Device Info */}
            <div className="space-y-2 pt-2 border-t border-border/30">
              <span className="font-body text-xs text-muted-foreground">Device Info:</span>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">WebGL:</span>
                  <span className={deviceInfo?.webgl ? 'text-success' : 'text-error'}>
                    {deviceInfo?.webgl ? 'Yes' : 'No'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Mobile:</span>
                  <span className="text-foreground">
                    {deviceInfo?.mobile ? 'Yes' : 'No'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Memory:</span>
                  <span className="text-foreground">{deviceInfo?.memory}GB</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Cores:</span>
                  <span className="text-foreground">{deviceInfo?.cores}</span>
                </div>
              </div>
            </div>

            {/* Current Settings */}
            <div className="pt-2 border-t border-border/30">
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">Particles:</span>
                <span className="text-foreground">{qualitySettings?.[quality]?.particles}</span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">Effects:</span>
                <span className="text-foreground">
                  {qualitySettings?.[quality]?.effects ? 'Enabled' : 'Disabled'}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PerformanceMonitor;